import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Download,
  Truck,
  ChevronDown,
  Calendar,
  FileText,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { useReport } from '../../hooks/useReport';
import { downloadCSV } from '../../utils/csv';
import { Button } from '../../components/ui/Button';

interface Report {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
}

const reports: Report[] = [
  {
    id: 'shipment',
    name: 'Shipment Report',
    icon: <Truck className="w-5 h-5" />,
    description:
      'Detailed log of all shipments with status, weight, and charges.',
  },
  // Other reports hidden for now as per user request
];

export default function ReportsPage() {
  const { user } = useAuth();
  const { getShipmentReportAsync, isFetchingReport } = useReport(
    user?.id as string
  );

  const [startDate, setStartDate] = useState<string>(
    format(new Date(), 'yyyy-MM-01')
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [selectedReport, setSelectedReport] = useState<string>('shipment');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleDownload = async () => {
    if (!startDate || !endDate || !selectedReport) {
      toast.error('Please select date range and report type');
      return;
    }

    try {
      if (selectedReport === 'shipment') {
        const res: any = await getShipmentReportAsync({
          startDate,
          endDate,
        });

        if (!res || !res.success) {
          toast.error(res?.message || 'Failed to generate report');
          return;
        }

        // Robust base64 decoding logic (same as Orders page)
        if (res.file) {
          try {
            const binaryString = atob(res.file);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const decoder = new TextDecoder('utf-8');
            const csv = decoder.decode(bytes);

            downloadCSV(csv, `shipment_report_${startDate}_to_${endDate}.csv`);
          } catch (e) {
            downloadCSV(
              res.file,
              `shipment_report_${startDate}_to_${endDate}.csv`
            );
          }
        } else if (res.url) {
          window.open(res.url, '_blank');
        } else if (res.data?.url) {
          window.open(res.data.url, '_blank');
        } else {
          toast.error('No downloadable file found in response');
          return;
        }

        toast.success('Report downloaded successfully');
      } else {
        toast.error('This report type is currently unavailable');
      }
    } catch (err) {
      console.error('Report download error:', err);
      toast.error('An error occurred while downloading the report');
    }
  };

  const selectedReportObj = reports.find((r) => r.id === selectedReport);

  return (
    <div className="min-h-screen bg-gray-50/30 px-4 lg:px-0 py-6 lg:py-10">
      <div className="mx-auto space-y-10">
        {/* Header with gradient subtle background */}
        <div className="relative overflow-hidden rounded-3xl bg-teal p-8 text-white shadow-xl">
          <div className="relative z-10 space-y-2">
            <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight">
              Business Intelligence Reports
            </h1>
            <p className="text-teal-50 text-lg max-w-2xl font-light">
              Export your shipment data, track performance, and gain actionable
              insights for your logistics operations.
            </p>
          </div>
          {/* Decorative circles */}
          <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 left-20 h-40 w-40 rounded-full bg-black/5 blur-2xl" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Left: Configuration Side */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Select date to get your shipment report
                </h3>
              </div>
              <div className="p-8 space-y-8">
                {/* Date Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 ml-1">
                      From Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full pl-4 pr-10 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 transition-all focus:border-teal focus:ring-4 focus:ring-teal/10 outline-none hover:border-gray-300"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 ml-1">
                      To Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full pl-4 pr-10 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 transition-all focus:border-teal focus:ring-4 focus:ring-teal/10 outline-none hover:border-gray-300"
                      />
                    </div>
                  </div>
                </div>

                {/* Report Type Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 ml-1">
                    Select Report Type
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full px-5 py-4 rounded-xl border border-gray-200 bg-white text-gray-900 text-left flex items-center justify-between transition-all hover:bg-gray-50 active:scale-[0.99]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center text-teal">
                          {selectedReportObj?.icon}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">
                            {selectedReportObj?.name}
                          </p>
                          <p className="text-xs text-gray-500 line-clamp-1">
                            {selectedReportObj?.description}
                          </p>
                        </div>
                      </div>
                      <ChevronDown
                        className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                          isDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-3 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 overflow-hidden animate-in fade-in slide-in-from-top-2">
                        {reports.map((report) => (
                          <button
                            key={report.id}
                            onClick={() => {
                              setSelectedReport(report.id);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full px-5 py-4 text-left flex items-center gap-4 hover:bg-teal/5 transition-colors ${
                              selectedReport === report.id
                                ? 'bg-teal/5 text-teal'
                                : 'text-gray-700'
                            }`}
                          >
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                selectedReport === report.id
                                  ? 'bg-teal text-white'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {report.icon}
                            </div>
                            <div>
                              <p className="font-bold">{report.name}</p>
                              <p className="text-xs opacity-70">
                                {report.description}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions / Tips */}
            <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100 flex gap-4 items-start">
              <div className="bg-amber-100 p-2 rounded-lg text-amber-700">
                <AlertCircle className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Right: Summary & Action */}
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
              <h3 className="font-bold text-xl text-gray-900">
                Report Summary
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Selected Report</span>
                  <span className="font-semibold text-gray-900">
                    {selectedReportObj?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Period</span>
                  <span className="font-semibold text-gray-900">
                    {startDate} to {endDate}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Format</span>
                  <span className="font-semibold text-gray-900 flex items-center gap-1">
                    CSV <FileText className="w-3 h-3" />
                  </span>
                </div>
              </div>

              <hr className="border-gray-50" />

              <Button
                size="lg"
                className="w-full bg-teal hover:bg-teal/90 text-white h-14 rounded-xl text-lg font-bold shadow-lg shadow-teal/10"
                onClick={handleDownload}
                disabled={isFetchingReport || !startDate || !endDate}
              >
                {isFetchingReport ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-3 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-3" />
                    Download Report
                  </>
                )}
              </Button>

              <p className="text-[10px] text-center text-gray-400">
                Your report will start downloading automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
